import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateIptvProviderRequest,
  IptvProvider,
  iptvProviderService,
  UpdateIptvProviderRequest,
} from '@/services/iptvProviderService';

export const iptvProviderKeys = {
  all: ['iptvProviders'] as const,
  list: () => [...iptvProviderKeys.all, 'list'] as const,
  channels: (providerId: string, updatedDate?: string) =>
    [...iptvProviderKeys.all, 'channels', providerId, updatedDate ?? 'unknown'] as const,
};

export const useIptvProviders = () => {
  return useQuery({
    queryKey: iptvProviderKeys.list(),
    queryFn: iptvProviderService.getProviders,
    select: (data) => data.providers,
  });
};

export const useIptvProviderChannels = (provider: IptvProvider | null | undefined) => {
  return useQuery({
    queryKey: iptvProviderKeys.channels(provider?.id ?? 'unknown', provider?.updatedDate),
    queryFn: () => {
      if (!provider) {
        throw new Error('Proveedor no encontrado');
      }

      return iptvProviderService.getProviderChannels(provider);
    },
    enabled: Boolean(provider),
  });
};

export const useCreateIptvProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateIptvProviderRequest) => iptvProviderService.createProvider(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: iptvProviderKeys.all });
    },
  });
};

export const useUpdateIptvProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateIptvProviderRequest) => iptvProviderService.updateProvider(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: iptvProviderKeys.all });
    },
  });
};

export const useDeleteIptvProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => iptvProviderService.deleteProvider(id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: iptvProviderKeys.list() });
      const previousProviders = queryClient.getQueryData<IptvProvider[]>(iptvProviderKeys.list());
      if (previousProviders) {
        queryClient.setQueryData(
          iptvProviderKeys.list(),
          previousProviders.filter((provider) => provider.id !== id)
        );
      }
      return { previousProviders };
    },
    onError: (_error, _id, context) => {
      if (context?.previousProviders) {
        queryClient.setQueryData(iptvProviderKeys.list(), context.previousProviders);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: iptvProviderKeys.all });
    },
  });
};
